import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { dispatch, toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useDownloadUrl } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { CreationWithTask } from '@/app/types';
import { generateFileName, reportEvent } from '@/app/utils';
import { downloadFilesAsZip } from '@/app/utils/client';

export type Item = Pick<CreationWithTask, 'id' | 'type' | 'nomark_uri' | 'uri'> & {
  fileName?: string;
  hasCopyright?: boolean;
};

export const useBatchDownload = (
  creations: Item[],

  onSuccess?: () => void,
  allHasCopyright?: boolean,
) => {
  const t = useTranslations();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadToastId = useRef<string | undefined>(undefined);
  const userInfo = useStore((state) => state.userInfo);
  const isFree = userInfo?.subs_plan === 'free';

  const { withWatermark: rawWithWatermark, setWithWatermark, getDownloadUrls } = useDownloadUrl();

  const withWatermark = rawWithWatermark || allHasCopyright;

  const handleBatchDownload = async () => {
    if (creations) {
      setIsDownloading(true);
      const blobUrlsToRevoke: string[] = []; // 跟踪需要释放的 Blob URLs

      // 检测是否有视频因版权限制无法去水印
      const hasCopyrightRestriction = !withWatermark && creations.some((item) => item.hasCopyright);

      // 批量获取下载 URL
      const downloadUrls = await getDownloadUrls(
        creations.map((item) => ({
          creationType: item.type,
          creationId: item.id,
          originalUri: item.uri,
          nomarkUri: item.nomark_uri,
          hasCopyright: item.hasCopyright,
          fileName:
            item.fileName ||
            generateFileName({
              type: item.type,
              id: item.id,
            }),
        })),
      );

      const selectedFiles = downloadUrls.map((url) => {
        // 本地生成的音频 Blob URL，记录以便后续释放
        if (url.config.creationType === 'audio' && SITE === 'china' && url.url.startsWith('blob:')) {
          blobUrlsToRevoke.push(url.url);
        }
        return {
          url: url.url,
          name: url.config.fileName || '',
        };
      });
      const creationIds = creations.map((item) => item.id).join(',');

      // 清理 Blob URLs 的函数
      const cleanupBlobUrls = () => {
        blobUrlsToRevoke.forEach((url) => {
          URL.revokeObjectURL(url);
        });
        blobUrlsToRevoke.length = 0; // 清空数组
      };

      // 多个文件打包下载
      const timestamp = new Date(Date.now() + (SITE === 'china' ? 8 : 0) * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 19)
        .replace(/[-T:]/g, '');
      downloadFilesAsZip(selectedFiles, {
        zipName: `vidu-${timestamp}.zip`,
        onBegin: () => {
          const { id } = toast({
            type: 'info',
            message: t('creations.batchOperation.compressing'),
            duration: Number.POSITIVE_INFINITY, // 无限期
          });
          downloadToastId.current = id;
        },
        onSuccess: () => {
          if (downloadToastId.current) {
            dispatch({ type: 'DISMISS_TOAST', toastId: downloadToastId.current });
            downloadToastId.current = undefined;
          }

          reportEvent('select_click', {
            select_name: withWatermark ? 'download_watermark' : 'download_no_watermark',
            select_amount: selectedFiles.length,
            creation_id: creationIds,
            is_success: true,
          });

          // 下载成功后释放 Blob URLs
          cleanupBlobUrls();

          onSuccess?.();
          setIsDownloading(false);

          // 根据是否有版权限制显示不同的提示
          toast({
            type: 'success',
            message: hasCopyrightRestriction
              ? t('creations.batchOperation.downloadSuccessWithCopyrightRestriction')
              : t('creations.batchOperation.downloadSuccess'),
          });
        },
        onFail: () => {
          if (downloadToastId.current) {
            dispatch({ type: 'DISMISS_TOAST', toastId: downloadToastId.current });
            downloadToastId.current = undefined;
          }

          reportEvent('select_click', {
            select_name: 'download_failed',
            select_amount: selectedFiles.length,
            creation_id: creationIds,
            is_success: false,
          });

          // 下载失败后也要释放 Blob URLs
          cleanupBlobUrls();

          setIsDownloading(false);
          toast({
            type: 'error',
            message: t('creations.batchOperation.downloadFail'),
          });
        },
      });
    }
  };

  return {
    isDownloading,
    isFree,
    withWatermark,
    setWithWatermark,
    handleBatchDownload,
  };
};
